import {
  addDoc,
  collection,
  getDocsFromServer,
  query,
  serverTimestamp,
  updateDoc,
  doc,
  where,
} from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { CATEGORY_TYPES } from '../domain/finance';
import { awaitRemoteConfirmation, requireConnectionForMutation } from './connectivityService';

export const CATEGORY_SCHEMA_VERSION = 1;
export const CATEGORY_NAME_MAX_LENGTH = 48;

export const normalizeCategoryName = (name) => String(name || '')
  .trim()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase('pt-BR');

export const validateCategoryInput = ({ name, type }) => {
  const trimmedName = String(name || '').trim();
  if (!trimmedName) throw new Error('CATEGORY_NAME_REQUIRED');
  if (trimmedName.length > CATEGORY_NAME_MAX_LENGTH) throw new Error('CATEGORY_NAME_TOO_LONG');
  if (!Object.values(CATEGORY_TYPES).includes(type)) throw new Error('INVALID_CATEGORY_TYPE');
  return { name: trimmedName, normalizedName: normalizeCategoryName(trimmedName), type };
};

const ensureUniqueName = async ({ accountId, normalizedName, ignoreId = null }, firestoreDb) => {
  const snapshot = await getDocsFromServer(query(
    collection(firestoreDb, 'accounts', accountId, 'categories'),
    where('normalizedName', '==', normalizedName)
  ));
  if (snapshot.docs.some((item) => item.id !== ignoreId)) throw new Error('CATEGORY_DUPLICATE');
};

export const createCategory = async ({ accountId, name, type, icon = '', colorToken = '' }, firestoreDb = firestore) => {
  if (!accountId) throw new Error('CATEGORY_ACCOUNT_REQUIRED');
  requireConnectionForMutation();
  const validated = validateCategoryInput({ name, type });
  await ensureUniqueName({ accountId, normalizedName: validated.normalizedName }, firestoreDb);
  const categoryRef = await awaitRemoteConfirmation(addDoc(collection(firestoreDb, 'accounts', accountId, 'categories'), {
    ...validated,
    active: true,
    icon: String(icon || '').slice(0, 40),
    colorToken: String(colorToken || '').slice(0, 40),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    schemaVersion: CATEGORY_SCHEMA_VERSION,
  }));
  return { id: categoryRef.id, ...validated, active: true, icon, colorToken, source: 'custom' };
};

export const updateCategory = async ({ accountId, categoryId, name, type, icon = '', colorToken = '' }, firestoreDb = firestore) => {
  if (!accountId || !categoryId) throw new Error('CATEGORY_REQUIRED');
  requireConnectionForMutation();
  const validated = validateCategoryInput({ name, type });
  await ensureUniqueName({ accountId, normalizedName: validated.normalizedName, ignoreId: categoryId }, firestoreDb);
  await awaitRemoteConfirmation(updateDoc(doc(firestoreDb, 'accounts', accountId, 'categories', categoryId), {
    ...validated,
    icon: String(icon || '').slice(0, 40),
    colorToken: String(colorToken || '').slice(0, 40),
    updatedAt: serverTimestamp(),
  }));
};

export const setCategoryActive = async ({ accountId, categoryId, active }, firestoreDb = firestore) => {
  if (!accountId || !categoryId || typeof active !== 'boolean') throw new Error('CATEGORY_REQUIRED');
  requireConnectionForMutation();
  await awaitRemoteConfirmation(updateDoc(doc(firestoreDb, 'accounts', accountId, 'categories', categoryId), {
    active,
    updatedAt: serverTimestamp(),
  }));
};
